import React, { useContext, useMemo, useRef } from "react";
import { useSwivuStore } from "../../../../hooks/useSwivuStore.ts";
import BryntumContext from "../../../../context/bryntumContext.jsx";
import { BaseModal } from "../../base/BaseModal.jsx";
import { OutcomeIncentiveConditionalFilters } from "./OutcomeIncentiveConditionalFilters.jsx";
import { OUTCOME_INCENTIVE_STATUS } from "@/other/constants/IncentiveKeys.jsx";
import { EDITOR_KEYS } from "../../../../../other/constants/EditorKeys.jsx";
import { REACT_EDITOR_CONTEXT_MENU_KEYS, SWIVU_ASSIST_CONFIG } from "../../../../../other/constants/OtherKeys.jsx";
import { INCENTIVE_PERMISSION_KEYS } from "@/other/constants/IncentiveKeys.jsx";
import { DateHelper } from "@bryntum/schedulerpro";
import { OutcomeIncentivePreview } from "./OutcomeIncentivePreview.jsx";
import { SwivuDuplicateEntityHelper } from "../../../SwivuDuplicateEntityHelper.jsx";
import { BaseModalBody } from "../../base/BaseModalBody.jsx";
import { useSwivuForm } from "../../../../hooks/useSwivuForm.jsx";
import { outcomeIncentiveSchema } from "../../../../schema/incentive/outcomeIncentiveSchema.jsx";
import { getBryntumRecordAsObject } from "../../../../../other/utils/utils.jsx";
import { checkIncentivePermission, handleUpdateIncentiveFilters, transformIncentiveFilters } from "../../../../../other/utils/incentiveUtils.jsx";
import { SwivuCombo } from "../../../SwivuCombo.jsx";
import { Controller } from "react-hook-form";
import { SwivuSeparator } from "../../../SwivuSeparator.jsx";
import { Switch } from "@/modern/components/ui/switch";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { cn } from "@/modern/utils";
import { DatePicker } from "@/modern/components/ui/date-picker";
import { RESOURCE_FILTER_FIELD_OPTIONS, TASK_FILTER_FIELD_OPTIONS, TASK_TAB_KEYS_BY_FIELD, RESOURCE_TAB_KEYS_BY_FIELD, INCENTIVE_FILTER_ENTITY_TYPES } from "@/other/constants/IncentiveKeys.jsx";
import { Card } from "@/modern/components/ui/card";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { startOfDay, endOfDay } from "date-fns";

const getFormDefaultValues = (record) => {
  return {
    taskFilter: transformIncentiveFilters(record.taskFilter),
    resourceFilter: transformIncentiveFilters(record.resourceFilter)
  };
};

const checkIfNameIsDuplicate = (store, name, excludeId) => {
  const record = store.query((record) => record.name === name && record.id !== excludeId, true);
  return record.length > 0;
};

export const OutcomeIncentiveEditor = (params) => {
  const { record } = params;
  const { incentiveRecord, pointRecord } = record;
  const editorParams = { ...params, record: incentiveRecord, pointRecord };
  const { version } = useSwivuStore({ storeId: STORE_KEYS.outcomeIncentiveStore, subscriberFn: ({ record }) => record.id === incentiveRecord.id });
  return <>{incentiveRecord.isLocked ? <OutcomeIncentivePreview {...editorParams} /> : <OutcomeIncentiveEditorWrapper {...editorParams} version={version} />}</>;
};

const IncentiveFieldHeading = ({ title, description }) => {
  return (
    <div className="flex flex-col md:flex-row md:items-baseline gap-2 mb-swivu-vertical">
      <h3 className="text-swivu-md font-semibold text-foreground mb-2 md:mb-0 mt-0">{title}</h3>
      <p className="text-muted-foreground md:text-swivu-sm m-0">{description}</p>
    </div>
  );
};

const EDITOR_SIZE = {
  width: "58rem",
  height: "min(100vh, 770px)"
};

const OutcomeIncentiveEditorWrapper = ({ record, onClose, editorInstance, version }) => {
  const { project, editorHandler } = useContext(BryntumContext);
  const filtersToDelete = useRef([]);

  const { form, formState } = useSwivuForm({ schema: outcomeIncentiveSchema, record, version, defaultValues: getFormDefaultValues(record) });

  const openSettingsEditor = (tabName) => {
    editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: tabName }, { by: "recordType", useExisting: true });
  };

  const onDeleteFilter = (filter) => {
    if (filter.recordId) filtersToDelete.current.push(filter);
  };

  const handleSave = (data) => {
    const { taskFilter, resourceFilter, ...payload } = data;
    const { outcomeIncentiveStore } = project;

    const duplicateRecord = checkIfNameIsDuplicate(outcomeIncentiveStore, data.name, record.id);
    if (duplicateRecord) {
      form.setError("name", {
        type: "manual",
        message: "An incentive with this name already exists"
      });
      return;
    }

    record.set(payload); // update record
    if (record.isPhantom) {
      outcomeIncentiveStore.add(record);
    }

    handleUpdateIncentiveFilters({
      store: project.outcomeIncentiveConditionsStore,
      resourceFilters: resourceFilter,
      taskFilters: taskFilter,
      incentiveId: record.id,
      filtersToDelete: filtersToDelete.current
    });

    onClose();
  };

  const createdByText = `Created by ${record.creatorUserName} on ${DateHelper.format(new Date(record.createdAt), "MM/DD/YYYY")}`;
  const editorTitle = record.isPhantom ? "New Incentive" : record.name;
  const editorDescription = record.isPhantom ? "Create a new incentive for your team" : createdByText;

  const memoizedPermissionMap = useMemo(() => {
    const permissionKeys = [INCENTIVE_PERMISSION_KEYS.INCENTIVE_MANAGERS, INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS, INCENTIVE_PERMISSION_KEYS.POINT_DELETERS];
    return permissionKeys.reduce((acc, permissionKey) => {
      acc[permissionKey] = checkIncentivePermission({ project, permissionKey });
      return acc;
    }, {});
  }, [project]);

  const hasManagePermission = memoizedPermissionMap[INCENTIVE_PERMISSION_KEYS.INCENTIVE_MANAGERS];
  const isDisabled = !hasManagePermission || !form.watch("name");
  const filtersHaveMissingValueRecords = useMemo(() => {
    return project?.outcomeIncentiveConditionsStore?.find((record) => record.incentiveId === record.id && !record.valueRecord, true); // This condition specifies that atleast one filter has missing value record which means its probably has been deleted
  }, [project, record.id]);

  const footerRenderer = () => {
    const currentStatus = form.watch("status");
    const isActive = currentStatus === OUTCOME_INCENTIVE_STATUS.ACTIVE;
    const isPaused = currentStatus === OUTCOME_INCENTIVE_STATUS.PAUSED;
    const isInactive = currentStatus === OUTCOME_INCENTIVE_STATUS.INACTIVE;
    const isSwitchDisabled = isDisabled || (isInactive && !filtersHaveMissingValueRecords);

    return (
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={isActive}
              onCheckedChange={(checked) => {
                const newStatus = checked ? OUTCOME_INCENTIVE_STATUS.ACTIVE : OUTCOME_INCENTIVE_STATUS.PAUSED;
                form.setValue("status", newStatus, { shouldValidate: true });
              }}
              disabled={isSwitchDisabled}
              className={cn("data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-amber-500", isInactive && "!bg-destructive opacity-80")}
              thumbClassName="bg-white"
            />
            <span
              className={cn(
                "text-sm font-semibold transition-colors",
                isActive && "text-green-600",
                isPaused && "text-amber-600",
                isInactive && "text-destructive",
                isSwitchDisabled && "opacity-50"
              )}
            >
              {isActive ? "Active" : isPaused ? "Paused" : "Inactive"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 ml-auto">
          <button
            type="button"
            onClick={() => {
              form.handleSubmit(handleSave)();
            }}
            className="p-swivu-button bg-primary hover:bg-primary/80 text-primary-foreground rounded-[0.6rem] transition-all font-medium shadow-lg hover:shadow-xl font-swivu-modern border-0 text-base cursor-pointer disabled:opacity-50 disabled:bg-muted disabled:text-muted-foreground disabled:pointer-events-none h-swivu-button leading-none"
            disabled={isDisabled || !form.watch("name")}
          >
            Save
          </button>
        </div>
      </div>
    );
  };

  return (
    <BaseModal
      title={editorTitle}
      description={editorDescription}
      onClose={onClose}
      size={{
        width: EDITOR_SIZE.width,
        height: EDITOR_SIZE.height,
        minHeight: EDITOR_SIZE.height,
        minWidth: EDITOR_SIZE.width
      }}
      record={record}
      footerRenderer={footerRenderer}
      version={version}
      contextMenuConfig={{
        [REACT_EDITOR_CONTEXT_MENU_KEYS.ARCHIVE]: {
          disabled: !memoizedPermissionMap[INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS]
        },
        [REACT_EDITOR_CONTEXT_MENU_KEYS.UNARCHIVE]: {
          disabled: !memoizedPermissionMap[INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS]
        },
        [REACT_EDITOR_CONTEXT_MENU_KEYS.DELETE]: {
          disabled: !memoizedPermissionMap[INCENTIVE_PERMISSION_KEYS.POINT_DELETERS],
          hidden: record.isPhantom
        }
      }}
      swivuAssistanceConfig={SWIVU_ASSIST_CONFIG.outcomeIncentive}
      editorInstance={editorInstance}
    >
      <BaseModalBody>
        {!hasManagePermission && (
          <div className="text-base font-medium font-swivu-modern text-foreground border-solid border-0 border-l-4 border-l-orange-400 bg-orange-50 dark:bg-orange-900/20 p-2 mb-swivu-vertical rounded-r-md">
            You don't have permission to manage incentives
          </div>
        )}
        <IncentiveFieldHeading title="Name the Incentive" description="Use a name that communicates eligibility and expected outcome" />
        <Controller
          control={form.control}
          name="name"
          render={({ field }) => (
            <SwivuDuplicateEntityHelper
              {...field}
              record={record}
              store={project.outcomeIncentiveStore}
              placeholder="Enter incentive name..."
              editorReactState={editorInstance}
              onChange={({ value, record }) => {
                if (record) {
                  const recordValues = getBryntumRecordAsObject(record);
                  form.reset({ ...recordValues, ...getFormDefaultValues(record) });
                } else {
                  form.setValue("name", value, { shouldValidate: true });
                }
              }}
              optionsFilterFn={(record) => {
                return !record.isLocked;
              }}
              error={formState.errors.name?.message}
              disabled={!hasManagePermission}
              editorRecordKey="incentiveRecord"
            />
          )}
        />
        <SwivuSeparator />

        <IncentiveFieldHeading title={`${record.isPhantom ? "Create" : "Edit"} Task Filters`} description="Define criteria for tasks that qualify for this incentive" />
        <OutcomeIncentiveConditionalFilters
          control={form.control}
          name="taskFilter"
          fieldOptions={TASK_FILTER_FIELD_OPTIONS}
          entityType={INCENTIVE_FILTER_ENTITY_TYPES.TASK}
          project={project}
          addButtonLabel="Add Task Filter"
          createOptionOnUnmatched={(field) => {
            openSettingsEditor(TASK_TAB_KEYS_BY_FIELD[field]);
          }}
          errors={formState.errors.taskFilter}
          isRequired={true}
          disabled={isDisabled}
          onDeleteFilter={onDeleteFilter}
          duplicateErrorMessage={formState.errors?.taskFilter?.root?.message}
        />

        <SwivuSeparator />
        <IncentiveFieldHeading title="Enter Dates and Points" description=" Start dates can be in the past. End date is optional." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Controller
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label="Start Date"
                  placeholder="Select start date"
                  error={formState.errors.startDate?.message}
                  disabled={isDisabled}
                  onChange={(date) => {
                    field.onChange(date ? startOfDay(date) : date);
                  }}
                />
              )}
            />
          </div>
          <div>
            <Controller
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label="End Date"
                  placeholder="Select end date"
                  error={formState.errors.endDate?.message}
                  disabled={isDisabled}
                  clearable={true}
                  onChange={(date) => {
                    field.onChange(date ? endOfDay(date) : date);
                  }}
                />
              )}
            />
          </div>
          <div>
            <Controller
              control={form.control}
              name="points"
              render={({ field }) => (
                <SwivuCombo {...field} type="number" label="Points Awarded" placeholder="Enter points..." isCombo={false} error={formState.errors.points?.message} disabled={isDisabled} />
              )}
            />
          </div>
        </div>

        <SwivuSeparator />

        <IncentiveFieldHeading title={`${record.isPhantom ? "Create" : "Edit"} Resource Filters`} description="Unless filtered, this incentive will apply to every scheduled resource" />
        <OutcomeIncentiveConditionalFilters
          control={form.control}
          name="resourceFilter"
          fieldOptions={RESOURCE_FILTER_FIELD_OPTIONS}
          entityType={INCENTIVE_FILTER_ENTITY_TYPES.RESOURCE}
          project={project}
          addButtonLabel="Add Resource Filter"
          clearable={true}
          createOptionOnUnmatched={(field) => {
            openSettingsEditor(RESOURCE_TAB_KEYS_BY_FIELD[field]);
          }}
          errors={formState.errors.resourceFilter}
          isRequired={false}
          emptyMessage="No filters applied - incentive will apply to all resources"
          disabled={isDisabled}
          onDeleteFilter={onDeleteFilter}
          duplicateErrorMessage={formState.errors?.resourceFilter?.root?.message}
        />

        <label htmlFor="multiplyByDays" className="text-base font-medium text-foreground block">
          <Card className="flex items-center gap-swivu-horizontal mt-swivu-vertical" variant="modern" size="base">
            <Controller
              control={form.control}
              name="multiplyByDays"
              render={({ field }) => <Checkbox id="multiplyByDays" checked={field.value} onCheckedChange={field.onChange} disabled={isDisabled} />}
            />
            <span>Multiply eligible resources by days-on-task</span>
          </Card>
        </label>
      </BaseModalBody>
    </BaseModal>
  );
};
